import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { TID } from "@/lib/testIds";

// The badge fixed to the bottom-right corner: a black disc with the studio's
// wordmark rotating around it and the mascot at its centre, opening WhatsApp.
//
// Restored from the pre-redesign build. Three things were fixed on the way back:
//  - it asked for "AvertaDemo", a font the project no longer loads, so the label
//    was silently falling back to a system serif;
//  - the rotation ran forever with no reduced-motion alternative;
//  - the WhatsApp number was hardcoded rather than read from CONTACT.
const CIRCLE_TEXT = "INICIAR PROJETO · ROCKFOX STUDIO ·";
const ROTATION_SECONDS = 15;

// O nome acessível tem de conter as palavras que estão visíveis no anel acima,
// não apenas descrever o destino. Quem usa comando de voz fala o que enxerga, e
// "clicar em rockfox studio" não encontrava nada: a marca aparecia na tela e não
// no nome. Era "Iniciar projeto no WhatsApp", que o Lighthouse acusou como
// label-content-name-mismatch — WCAG 2.5.3.
//
const ACCESSIBLE_NAME = "Iniciar projeto · RockFox Studio — ir para a página de contato";

const RotatingLabel = ({ animate }) => (
    <motion.div
        className="pointer-events-none absolute inset-0"
        animate={animate ? { rotate: 360 } : undefined}
        transition={animate ? { duration: ROTATION_SECONDS, ease: "linear", repeat: Infinity } : undefined}
        aria-hidden
    >
        <svg viewBox="0 0 120 120" className="h-full w-full">
            <defs>
                <path id="floating-cta-circle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
            </defs>
            {/* White on the brand orange, as asked. Bricolage Grotesque with the
                project's display variation (opsz 24 / wdth 87.5) so the ring reads
                as the studio's own lettering rather than a generic label.
                Bumped to 11px/700 for legibility: white on #EA6D11 measures
                3.14:1, which is under the 4.5:1 that small text normally wants. */}
            <text
                fill="#ffffff"
                style={{
                    fontFamily: '"Bricolage Grotesque", system-ui, sans-serif',
                    fontVariationSettings: '"opsz" 24, "wdth" 87.5',
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                }}
            >
                <textPath href="#floating-cta-circle" startOffset="0%" textLength="276" lengthAdjust="spacing">
                    {CIRCLE_TEXT}
                </textPath>
            </text>
        </svg>
    </motion.div>
);

const MotionLink = motion.create(Link);

export const FloatingCTA = () => {
    const reduceMotion = useReducedMotion();

    return (
        <MotionLink
            to="/contato"
            data-testid={TID.floatingCta}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.8 }}
            animate={reduceMotion ? false : { opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="group fixed bottom-6 right-6 z-[60] flex h-[105px] w-[105px] items-center justify-center rounded-full bg-brand shadow-[0_8px_30px_rgba(0,0,0,0.18)] md:bottom-8 md:right-8 md:h-[115px] md:w-[115px]"
            aria-label={ACCESSIBLE_NAME}
        >
            {/* The label still reads when it is not spinning, so reduced motion
                simply holds it still rather than hiding it. */}
            <RotatingLabel animate={!reduceMotion} />

            <div className="relative flex h-[50px] w-[50px] items-center justify-center overflow-hidden rounded-full bg-paper shadow-inner transition-transform duration-500 group-hover:scale-95 md:h-[56px] md:w-[56px]">
                {/* A 160px copy, not the 870px one the studio page uses. This
                    badge is on every page and draws at 48px, so the full-size
                    file was 128KB to paint an area the size of a thumbnail —
                    18x more pixels than the screen can show. The small copy is
                    15KB, and at this scale the two are indistinguishable.

                    Two files rather than one shared file on purpose: most
                    visitors never open /sobre, so paying 128KB here to save a
                    second request there is the wrong trade. */}
                <img
                    src="/images/brand/mascote-badge.webp"
                    alt=""
                    aria-hidden
                    width={160}
                    height={166}
                    className="pointer-events-none h-[85%] w-[85%] select-none object-contain"
                />
            </div>
        </MotionLink>
    );
};
